import { useState } from "react"
import {
  Milestone,
  MilestoneStatus,
  STATUS_META,
  KANBAN_COLUMNS,
  formatDate,
  getTodoProgress,
  getAssignees,
} from "../hooks/useMilestones"
import { MilestoneTodoChecklist } from "./MilestoneTodoChecklist"

interface MilestoneKanbanProps {
  milestones: Milestone[]
  onEdit: (m: Milestone) => void
  onStatusChange: (m: Milestone, status: MilestoneStatus) => void
  /** 세부 To do 체크 토글 (없으면 읽기 전용) */
  onToggleTodo?: (m: Milestone, todoId: string) => Promise<void> | void
}

export function MilestoneKanban({
  milestones,
  onEdit,
  onStatusChange,
  onToggleTodo,
}: MilestoneKanbanProps) {
  // 카드별 체크리스트 펼침 상태
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [savingId, setSavingId] = useState<string | null>(null)

  const grouped = KANBAN_COLUMNS.map((status) => ({
    status,
    items: milestones.filter((m) => m.status === status),
  }))

  const handleDrop = (e: React.DragEvent, status: MilestoneStatus) => {
    e.preventDefault()
    const id = e.dataTransfer.getData("text/milestone-id")
    const target = milestones.find((m) => m._id === id)
    if (target && target.status !== status) {
      onStatusChange(target, status)
    }
  }

  const handleToggle = async (m: Milestone, todoId: string) => {
    if (!onToggleTodo) return
    setSavingId(m._id)
    try {
      await onToggleTodo(m, todoId)
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-4" style={{ minWidth: KANBAN_COLUMNS.length * 260 }}>
        {grouped.map(({ status, items }) => {
          const meta = STATUS_META[status]
          return (
            <div
              key={status}
              className="flex-1 min-w-[240px] bg-gray-50 rounded-xl border border-gray-200 flex flex-col"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, status)}
            >
              {/* 컬럼 헤더 */}
              <div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                  <span className="text-sm font-bold text-gray-800">{meta.label}</span>
                </div>
                <span className="text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                  {items.length}
                </span>
              </div>

              {/* 카드 목록 */}
              <div className="p-2.5 space-y-2.5 flex-1 min-h-[120px]">
                {items.length === 0 ? (
                  <div className="py-6 text-center text-xs text-gray-400">
                    카드를 끌어다 놓으세요
                  </div>
                ) : (
                  items.map((m) => {
                    const todos = m.todos || []
                    const todoTotal = todos.length
                    const todoDone = todos.filter((t) => t?.done).length
                    const todoPercent = getTodoProgress(todos)
                    // 체크리스트가 있으면 그 진행률을 우선 표시
                    const progress =
                      todoPercent !== null
                        ? todoPercent
                        : Math.min(100, Math.max(0, m.progress ?? 0))
                    const isOverdue =
                      m.status !== "completed" &&
                      m.status !== "archive" &&
                      m.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10)
                    const isOpen = Boolean(expanded[m._id])
                    const assignees = getAssignees(m)
                    // 카드가 좁으므로 2명까지만 노출하고 나머지는 +N 으로 축약
                    const shownAssignees = assignees.slice(0, 2)
                    const restAssignees = assignees.length - shownAssignees.length
                    const assigneeTitle = assignees.map((a) => a.userName).join(", ")

                    return (
                      <div
                        key={m._id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/milestone-id", m._id)}
                        onClick={() => onEdit(m)}
                        className={`bg-white rounded-lg border ${meta.border} p-3 shadow-sm hover:shadow-md cursor-pointer transition-shadow`}
                      >
                        {/* 프로젝트명 */}
                        {m.projectName ? (
                          <div className="text-[10px] font-semibold text-orange-600 uppercase tracking-wide mb-1 truncate">
                            {m.projectName}
                          </div>
                        ) : null}

                        {/* 제목 */}
                        <div className="text-sm font-semibold text-gray-900 mb-1.5 leading-snug">
                          {m.title}
                        </div>

                        {/* 산출물 */}
                        {m.deliverable ? (
                          <div className="text-[11px] text-gray-600 mb-2 flex items-start gap-1">
                            <svg className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="line-clamp-2">{m.deliverable}</span>
                          </div>
                        ) : null}

                        {/* 진행률 바 */}
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                          <div className={`h-full ${meta.bar} rounded-full`} style={{ width: `${progress}%` }} />
                        </div>

                        {/* 세부 To do */}
                        {todoTotal > 0 ? (
                          <div className="mb-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                setExpanded((prev) => ({ ...prev, [m._id]: !prev[m._id] }))
                              }}
                              className="w-full flex items-center justify-between gap-2 px-1.5 py-1 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700">
                                <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                                To do {todoDone}/{todoTotal}
                              </span>
                              <svg
                                className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>

                            {isOpen ? (
                              <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                                <MilestoneTodoChecklist
                                  todos={todos}
                                  compact
                                  saving={savingId === m._id}
                                  onToggle={
                                    onToggleTodo ? (todoId) => handleToggle(m, todoId) : undefined
                                  }
                                />
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {/* 하단 메타 */}
                        <div className="flex items-center justify-between text-[10px]">
                          <span className={isOverdue ? "text-red-600 font-bold" : "text-gray-500"}>
                            {formatDate(m.dueDate)}
                            {isOverdue ? " (지연)" : ""}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {(m.slackDays ?? 0) > 0 ? (
                              <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 font-semibold">
                                +{m.slackDays}d
                              </span>
                            ) : null}
                            {assignees.length > 0 ? (
                              <span
                                className="flex items-center gap-1 max-w-[120px]"
                                title={`담당자: ${assigneeTitle}`}
                              >
                                {shownAssignees.map((a, i) => (
                                  <span
                                    key={`${a.userId || a.userName}-${i}`}
                                    className="text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 truncate"
                                  >
                                    {a.userName}
                                  </span>
                                ))}
                                {restAssignees > 0 ? (
                                  <span className="text-gray-500 bg-gray-100 rounded px-1 py-0.5 shrink-0">
                                    +{restAssignees}
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
